import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from "@headlessui/react";
import { Download, ExternalLink, Eye, X } from "lucide-react";
import clsx from "clsx";
import { Column } from "types/Datatable";
import { PurchaseDocListItem } from "types/InvoiceInbox";
import { purchaseDocContentUrl } from "services/invoice-inbox.service";
import { touchHitAreaClass } from "./invoice-doc-ui";

/**
 * The file, and nothing else.
 *
 * The register answers "which invoice is this?" only through fields the model
 * read out of it — so the first question while triaging a queue is "let me just
 * look at the PDF". Opening the full document panel for that is a round-trip
 * through a second fetch and a screen full of actions; this is a peek that
 * closes with Esc and leaves the list, its page and its scroll exactly where
 * they were.
 */

/**
 * What the browser will actually render inline.
 *
 * Deliberately mirrors the `isBrowsable` list in the backend's
 * files/download.js, which this module used to frame: a type outside it
 * (a .tiff, an .xml) is either downloaded or left blank by an <iframe> — an
 * empty frame to the person looking at it. The register's own content
 * endpoints now serve the real mime type, but what a browser can SHOW is the
 * same short list, so it still decides here: by the file name's extension,
 * with the recorded mime type as a fallback.
 */
const INLINE_EXTENSIONS = ["pdf", "jpg", "jpeg", "png", "gif", "txt"];
const INLINE_MIME_TYPES = [
  "application/pdf",
  "image/jpeg",
  "image/png",
  "image/gif",
  "text/plain",
];

function extensionOf(fileName: string | null): string {
  if (!fileName) return "";
  const dot = fileName.lastIndexOf(".");
  if (dot < 0) return "";
  return fileName.slice(dot + 1).toLowerCase();
}

export function canPreviewInline(item: {
  fileName?: string | null;
  mimeType?: string | null;
}): boolean {
  const extension = extensionOf(item.fileName || null);
  if (extension) return INLINE_EXTENSIONS.includes(extension);
  return INLINE_MIME_TYPES.includes((item.mimeType || "").toLowerCase());
}

export function isImage(item: { fileName?: string | null; mimeType?: string | null }): boolean {
  const extension = extensionOf(item.fileName || null);
  if (extension) return ["jpg", "jpeg", "png", "gif"].includes(extension);
  return (item.mimeType || "").toLowerCase().startsWith("image/");
}

/**
 * Whether the document's primary file can be streamed yet: the OneDrive copy
 * (`file`) or, before that exists, the intake's local copy (`storagePath`) —
 * the content endpoint serves either.
 */
export function hasPrimaryFile(doc: { file?: string | null; storagePath?: string | null }): boolean {
  return Boolean(doc.file || doc.storagePath);
}

/**
 * The column that opens it, shared by the flat register and the month tree —
 * the same peek has to be in both, and a second copy of it is how the two
 * views start behaving differently.
 *
 * First, not last: the flags column grows with badges and pushes the far side
 * of the table off-screen, and this is the action reached most often while
 * triaging — it has to be where the eye already is.
 */
export function buildPreviewColumn(
  t: (key: string, options?: any) => string,
  onPreview: (item: PurchaseDocListItem) => void
): Column {
  return {
    key: "preview",
    name: t("invoice_inbox_col_preview", { defaultValue: "File" }),
    type: "custom",
    render: (item: PurchaseDocListItem) =>
      hasPrimaryFile(item) ? (
        <button
          type="button"
          // The whole row is a link to the document panel; without this the
          // peek would navigate away from the list at the same time.
          onClick={(event) => {
            event.stopPropagation();
            onPreview(item);
          }}
          title={t("invoice_inbox_preview", { defaultValue: "Preview the file" })}
          aria-label={t("invoice_inbox_preview", { defaultValue: "Preview the file" })}
          className={clsx(
            "inline-flex items-center justify-center rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-900",
            touchHitAreaClass
          )}
        >
          <Eye className="h-4 w-4" />
        </button>
      ) : (
        <span className="text-gray-300">—</span>
      ),
  };
}

type Props = {
  companyId: string;
  doc: PurchaseDocListItem;
  /** Where "open the document" goes — the same row URL the table navigates to. */
  docUrl: string;
  onClose: () => void;
};

export default function InvoiceDocPreview({ companyId, doc, docUrl, onClose }: Props) {
  const { t } = useTranslation();
  const hasFile = hasPrimaryFile(doc);
  // The register's own content endpoint, not the files one: it works before
  // the OneDrive copy exists and answers with the real content type.
  const viewUrl = hasFile ? purchaseDocContentUrl(companyId, doc.id) : "";
  const downloadUrl = hasFile ? `${purchaseDocContentUrl(companyId, doc.id)}?download=1` : "";
  const openDocText = t("invoice_inbox_preview_open_doc", { defaultValue: "Open the document" });
  const openFileText = t("invoice_inbox_preview_open_file", { defaultValue: "Open the file separately" });
  const title =
    doc.fileName ||
    doc.invoiceNumber ||
    doc.supplierNameRaw ||
    t("invoice_inbox_preview", { defaultValue: "Preview" });

  return (
    <Dialog open onClose={onClose} className="relative z-[1000]">
      <DialogBackdrop className="fixed inset-0 bg-zinc-950/50" />
      <div className="fixed inset-0 flex items-center justify-center p-2 sm:p-6">
        <DialogPanel className="flex h-full w-full max-w-6xl flex-col overflow-hidden rounded-xl bg-white shadow-xl ring-1 ring-zinc-950/10">
          <div className="flex items-center gap-3 border-b border-gray-200 px-4 py-3">
            <DialogTitle className="min-w-0 flex-1 truncate text-sm font-semibold text-gray-900">
              {title}
            </DialogTitle>
            {/* Below sm the two links are icons, so the file's name stays readable. */}
            <Link
              to={docUrl}
              onClick={onClose}
              aria-label={openDocText}
              className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-sm text-blue-700 hover:bg-blue-50 max-sm:p-3.5"
            >
              <ExternalLink className="h-4 w-4" />
              <span className="hidden sm:inline">{openDocText}</span>
            </Link>
            {viewUrl && (
              // The VIEW url, not the download one — "open separately" means
              // exactly that: the same inline PDF/image, in its own tab, not
              // a forced download. A NEW TAB, not this one: a plain same-tab
              // link would replace the register with the file and lose the
              // page and scroll the peek exists to preserve. `?download=1` is
              // for the explicit download button alone.
              <a
                href={viewUrl}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={openFileText}
                className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-sm text-gray-700 hover:bg-gray-100 max-sm:p-3.5"
              >
                <Download className="h-4 w-4" />
                <span className="hidden sm:inline">{openFileText}</span>
              </a>
            )}
            <button
              type="button"
              onClick={onClose}
              aria-label={t("close", { defaultValue: "Close" })}
              className="rounded-md p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-900 max-sm:p-3"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="min-h-0 flex-1 bg-gray-100">
            {!hasFile ? (
              <div className="p-6 text-sm text-gray-500">
                {t("invoice_inbox_file_pending", {
                  defaultValue: "The file is still being filed away — reload in a moment.",
                })}
              </div>
            ) : !canPreviewInline(doc) ? (
              // An .xml, .zip or .heic attachment is a real document here — it
              // just cannot be shown, so say so instead of framing a download.
              <div className="flex h-full flex-col items-center justify-center gap-3 p-6 text-center">
                <p className="text-sm text-gray-600">
                  {t("invoice_inbox_preview_unsupported", {
                    defaultValue: "This file type cannot be shown in the browser.",
                  })}
                </p>
                <a
                  href={downloadUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  download={doc.fileName || undefined}
                  className="inline-flex items-center gap-1 rounded-md bg-gray-900 px-3 py-2 text-sm font-medium text-white hover:bg-gray-700"
                >
                  <Download className="h-4 w-4" />
                  {t("download_file", { defaultValue: "Download file" })}
                </a>
              </div>
            ) : isImage(doc) ? (
              <div className="h-full overflow-auto p-4 text-center">
                <img src={viewUrl} alt={title} className="mx-auto max-w-full" />
              </div>
            ) : (
              <iframe title={title} src={viewUrl} className="h-full w-full border-0" />
            )}
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
}
